import React, { FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, Truck, ArrowRight, AlertTriangle, PackageMinus } from "lucide-react";
import { ROUTES_CONFIG } from "src/constants/routesConfig";
import { QUANTITY_CHANGE_REASON_LABEL } from "src/constants/relabelCases";
import { CHILD_PO_DEFAULT_SENDER_EMAIL } from "src/constants";
import { ValidationErrors } from "src/errors";
import { ProductColumnHeader } from "src/enums";
import { isInvalidISODate } from "src/helpers/dateHelper";
import {
  EuRelabelCheckInFormData,
  EuRelabelCheckInFormDataKey,
  EuRelabelCheckInProductDateType,
  EuRelabelCheckInProductRow,
  EuRelabelCheckInProductRowShortage,
  PurchaseOrder,
  TransferOrder,
  EuRelabelCheckIn
} from "src/types";
import { useScrollOnMessage } from "src/hooks";
import { TextInputBox, DateInputBox, SelectInputBox, SelectOption } from "src/components/ui/inputs";
import { FormLvlErrorMessage, ValidationErrorMessage } from "src/components/ui/feedback";
import { Table, ControlButton } from "src/components";
import { TableRowHighlight } from "src/components/ui/table";
import { CollapsibleSection } from "src/components/ui/collapsible";
import { formatEstimatedDeliveryDate } from "src/components/tables/dashboardTables/shared/dateFormatters";
import { LoggedShortagesList } from "./loggedShortagesList";
import { LogShortageModal } from "./logShortageModal";
import { RoutingInstructionCards } from "./routingInstructionCards";

const PRODUCT_DATE_TYPE_OPTIONS: SelectOption[] = [
  { value: "", label: "——" },
  { value: "BBE", label: "BBE" },
  { value: "Use By", label: "Use By" },
  { value: "Best By", label: "Best By" },
  { value: "MFG", label: "MFG" }
];

export type EuRelabelCheckInSourceOrder =
  | { kind: "PO"; purchaseOrder: PurchaseOrder }
  | { kind: "TO"; transferOrder: TransferOrder };

interface EuRelabelCheckInFormProps {
  formData: EuRelabelCheckInFormData;
  sourceOrder: EuRelabelCheckInSourceOrder;
  gxoEuCheckInQtyByProductId: Record<string, number>;
  validationErrors?: ValidationErrors<EuRelabelCheckInFormDataKey>;
  formLvlErrorMessage: string;
  overageSubmitNonce: string | null;
  isFormUpdated: boolean;
  existingCheckIns: EuRelabelCheckIn[];
  newCheckIn?: EuRelabelCheckIn;
  handleProductReceivedQtyChange: (productIndex: number, receivedQty: string) => void;
  handleProductDateChange: (productIndex: number, productDate: string) => void;
  handleProductDateTypeChange: (productIndex: number, dateType: EuRelabelCheckInProductDateType | "") => void;
  handleProductLotNumberChange: (productIndex: number, lotNumber: string) => void;
  handleProductShortageChange: (productIndex: number, shortage: EuRelabelCheckInProductRowShortage | undefined) => void;
  handleReceivedAllRemaining: () => void;
  handleSubmit: (e?: FormEvent) => Promise<void>;
}

const EuRelabelCheckInForm: React.FC<EuRelabelCheckInFormProps> = ({
  formData,
  sourceOrder,
  gxoEuCheckInQtyByProductId,
  validationErrors,
  formLvlErrorMessage,
  overageSubmitNonce,
  isFormUpdated,
  existingCheckIns,
  newCheckIn,
  handleProductReceivedQtyChange,
  handleProductDateChange,
  handleProductDateTypeChange,
  handleProductLotNumberChange,
  handleProductShortageChange,
  handleReceivedAllRemaining,
  handleSubmit
}) => {
  const formLvlErrorMessageRef = useScrollOnMessage(formLvlErrorMessage);
  const overageProducts = formData.products.filter((p) => p.receivedQty !== "" && Number(p.receivedQty) > p.remaining);
  const showOverageSubmitError = !!overageSubmitNonce && overageProducts.length > 0;
  const overageErrorRef = useScrollOnMessage(showOverageSubmitError ? overageSubmitNonce : undefined);

  const [shortageModalProductId, setShortageModalProductId] = useState<string | null>(null);
  const shortageModalRow =
    shortageModalProductId !== null ? formData.products.find((p) => p.id === shortageModalProductId) : undefined;

  const isPO = sourceOrder.kind === "PO";
  const sourceOrderId = isPO ? sourceOrder.purchaseOrder.id : sourceOrder.transferOrder.id;
  const sourceOrderTypeLabel = isPO ? "Purchase Order" : "Transfer Order";
  const sourceOrderIdLabel = isPO ? "PO ID" : "TO ID";
  const sourceOrderVendorName = isPO ? (sourceOrder.purchaseOrder.vendorName ?? "N/A") : "Internal Transfer";
  const sourceOrderProductCount = isPO
    ? (sourceOrder.purchaseOrder.products?.length ?? 0)
    : sourceOrder.transferOrder.products.length;
  const estDeliveryDate = isPO
    ? formatEstimatedDeliveryDate(
        sourceOrder.purchaseOrder.carrierEstimatedDeliveryDate,
        sourceOrder.purchaseOrder.confirmationDetails?.estDeliveryDate,
        sourceOrder.purchaseOrder.estimatedDeliveryDate
      )
    : undefined;

  const totalReceivedThisBatch = formData.products.reduce((sum, p) => sum + (Number(p.receivedQty) || 0), 0);
  const totalRemaining = formData.products.reduce((sum, p) => sum + p.remaining, 0);
  const totalPrevReceived = formData.products.reduce((sum, p) => sum + p.prevReceivedQty, 0);
  const totalExpected = formData.products.reduce((sum, p) => sum + p.totalExpected, 0);
  const previousCheckIns = newCheckIn ? existingCheckIns.filter((c) => c.id !== newCheckIn.id) : existingCheckIns;

  const showGxoEuCheckInColumn = Object.keys(gxoEuCheckInQtyByProductId).length > 0;
  const showPrevShortageColumn = formData.products.some((p) => p.prevShortageQty > 0);

  const gxoEuCheckInColumn = {
    header: "GXO check-in (EU)" as ProductColumnHeader,
    column: (row: EuRelabelCheckInProductRow) => (
      <span className="font-semibold text-secondary-900 tabular-nums">{gxoEuCheckInQtyByProductId[row.id] ?? 0}</span>
    ),
    styles: { minWidth: "130px", textAlign: "center" as const }
  };

  const confirmedEuColumn = {
    header: "Confirmed (EU)" as ProductColumnHeader,
    column: (row: EuRelabelCheckInProductRow) =>
      row.confirmedEuQty !== undefined ? (
        <span className="font-semibold text-secondary-900 tabular-nums">{row.confirmedEuQty}</span>
      ) : (
        <span className="text-sm text-secondary-400 tabular-nums">—</span>
      ),
    styles: { minWidth: "130px", textAlign: "center" as const }
  };

  const prevShortageColumn = {
    header: "Prev. Short" as ProductColumnHeader,
    column: (row: EuRelabelCheckInProductRow) =>
      row.prevShortageQty > 0 ? (
        <span className="text-sm font-semibold text-amber-700 tabular-nums">{row.prevShortageQty}</span>
      ) : (
        <span className="text-sm text-secondary-400 tabular-nums">—</span>
      ),
    styles: { minWidth: "110px", textAlign: "center" as const }
  };

  const productTableColumns = [
    {
      header: ProductColumnHeader.ID,
      column: (row: EuRelabelCheckInProductRow) => <span className="font-mono text-secondary-700">{row.id}</span>,
      styles: { minWidth: "100px" }
    },
    {
      header: ProductColumnHeader.NAME,
      column: (row: EuRelabelCheckInProductRow) => <div>{row.name}</div>,
      styles: { minWidth: "200px" }
    },
    {
      header: ProductColumnHeader.BARCODE,
      column: (row: EuRelabelCheckInProductRow) => <span className="font-mono text-secondary-600">{row.barcode}</span>,
      sticky: true
    },
    {
      header: ProductColumnHeader.SKU,
      column: (row: EuRelabelCheckInProductRow) => <span className="font-mono text-secondary-600">{row.skuName}</span>,
      styles: { minWidth: "130px" }
    },
    {
      header: ProductColumnHeader.SIZE,
      column: (row: EuRelabelCheckInProductRow) => <div>{row.size ?? "-"}</div>,
      styles: { minWidth: "90px" }
    },
    {
      header: "Order Qty (EU)" as ProductColumnHeader,
      column: (row: EuRelabelCheckInProductRow) => (
        <span className="font-semibold text-secondary-900 tabular-nums">{row.orderQty}</span>
      ),
      styles: { minWidth: "100px", textAlign: "center" as const }
    },
    confirmedEuColumn,
    ...(showGxoEuCheckInColumn ? [gxoEuCheckInColumn] : []),
    {
      header: "Prev. Received" as ProductColumnHeader,
      column: (row: EuRelabelCheckInProductRow) => (
        <span className="text-sm text-secondary-500 tabular-nums">{row.prevReceivedQty}</span>
      ),
      styles: { minWidth: "120px", textAlign: "center" as const }
    },
    ...(showPrevShortageColumn ? [prevShortageColumn] : []),
    {
      header: "Receive Now" as ProductColumnHeader,
      column: (row: EuRelabelCheckInProductRow) => {
        const productIndex = formData.products.findIndex((p) => p.id === row.id);
        const inputNum = Number(row.receivedQty) || 0;
        const isOver = row.receivedQty !== "" && inputNum > row.remaining;
        return (
          <div className="w-24">
            <TextInputBox
              id={`received-qty-${row.id}`}
              value={row.receivedQty}
              onChange={(value) => handleProductReceivedQtyChange(productIndex, value)}
              maxLength={10}
              placeholder={`/ ${row.remaining}`}
              size="sm"
              error={isOver}
            />
            {isOver && (
              <p className="text-xs text-amber-600 mt-0.5 whitespace-nowrap">+{inputNum - row.remaining} over</p>
            )}
          </div>
        );
      },
      styles: { minWidth: "130px", textAlign: "center" as const }
    },
    {
      header: "Date" as ProductColumnHeader,
      column: (row: EuRelabelCheckInProductRow) => {
        const productIndex = formData.products.findIndex((p) => p.id === row.id);
        const hasDate = row.productDate !== "";
        const hasType = row.dateType !== "";
        const isMismatched = hasDate !== hasType;
        const isDateInvalid = hasDate && isInvalidISODate(row.productDate);
        return (
          <div className="flex items-center gap-2">
            <div className="w-24 shrink-0">
              <SelectInputBox
                id={`date-type-${row.id}`}
                value={row.dateType || undefined}
                options={PRODUCT_DATE_TYPE_OPTIONS}
                onChange={(value) =>
                  handleProductDateTypeChange(productIndex, value as EuRelabelCheckInProductDateType | "")
                }
                placeholder="Type"
                size="sm"
                error={isMismatched && !hasType}
              />
            </div>
            <div className="w-36 shrink-0">
              <DateInputBox
                id={`product-date-${row.id}`}
                value={row.productDate}
                onChange={(value) => handleProductDateChange(productIndex, value)}
                error={isDateInvalid}
              />
            </div>
          </div>
        );
      },
      styles: { minWidth: "450px", textAlign: "center" as const }
    },
    {
      header: "Lot Number" as ProductColumnHeader,
      column: (row: EuRelabelCheckInProductRow) => {
        const productIndex = formData.products.findIndex((p) => p.id === row.id);
        return (
          <div className="w-32">
            <TextInputBox
              id={`lot-number-${row.id}`}
              value={row.lotNumber}
              onChange={(value) => handleProductLotNumberChange(productIndex, value)}
              placeholder="e.g. LOT-001"
              maxLength={100}
              size="sm"
            />
          </div>
        );
      },
      styles: { minWidth: "150px" }
    },
    ...(isPO
      ? [
          {
            header: "New Shortage" as ProductColumnHeader,
            column: (row: EuRelabelCheckInProductRow) => {
              if (row.shortage) {
                return (
                  <button
                    type="button"
                    onClick={() => setShortageModalProductId(row.id)}
                    className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 transition-colors"
                    title="Edit logged shortage"
                  >
                    <PackageMinus className="w-3 h-3" />
                    <span className="font-semibold tabular-nums">{row.shortage.shortageQty}</span>
                    <span className="text-amber-600">·</span>
                    <span>{QUANTITY_CHANGE_REASON_LABEL[row.shortage.reason]}</span>
                  </button>
                );
              }
              return (
                <button
                  type="button"
                  onClick={() => setShortageModalProductId(row.id)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-secondary-200 bg-white px-2 py-1 text-xs font-medium text-secondary-600 hover:border-amber-300 hover:bg-amber-50 hover:text-amber-700 transition-colors"
                >
                  <PackageMinus className="w-3 h-3" />
                  Log shortage
                </button>
              );
            },
            styles: { minWidth: "200px", textAlign: "center" as const }
          }
        ]
      : [])
  ];

  return (
    <div className="px-2 sm:px-3 lg:px-4">
      <div className="mx-auto">
        <div className="overflow-hidden rounded-2xl border border-secondary-200 shadow-soft bg-white">
          <form onSubmit={handleSubmit} autoComplete="off" className="p-6 sm:p-8">
            <div className="space-y-6">
              <div className="rounded-2xl border border-secondary-200 bg-white shadow-sm overflow-hidden">
                <div className="bg-black px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="rounded-lg bg-white/10 p-2">
                      <ClipboardList className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-white tracking-wide">{sourceOrderTypeLabel} Details</h3>
                      <p className="text-xs text-gray-400 mt-0.5">
                        Overview of the selected {sourceOrderTypeLabel.toLowerCase()}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-secondary-100 rounded-xl p-4 border border-secondary-200">
                      <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wide mb-1.5">
                        {sourceOrderIdLabel}
                      </p>
                      <p className="text-sm font-bold text-secondary-900 font-mono">{sourceOrderId}</p>
                    </div>
                    <div className="bg-secondary-100 rounded-xl p-4 border border-secondary-200">
                      <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wide mb-1.5">Vendor</p>
                      <p className="text-sm font-bold text-secondary-900">{sourceOrderVendorName}</p>
                    </div>
                    <div className="bg-secondary-100 rounded-xl p-4 border border-secondary-200">
                      <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wide mb-1.5">
                        Total SKUs
                      </p>
                      <p className="text-sm font-bold text-secondary-900">{sourceOrderProductCount}</p>
                    </div>
                    {isPO && (
                      <div className="bg-secondary-100 rounded-xl p-4 border border-secondary-200">
                        <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wide mb-1.5">
                          Est. Delivery
                        </p>
                        <p className="text-sm font-bold text-secondary-900">{estDeliveryDate ?? "N/A"}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-secondary-200 bg-white shadow-sm overflow-hidden">
                <div className="bg-black px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="rounded-lg bg-white/10 p-2">
                      <Truck className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-white tracking-wide">Product Details</h3>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {formData.products.length} product{formData.products.length !== 1 ? "s" : ""} &middot;{" "}
                        {totalPrevReceived} of {totalExpected} units received ({existingCheckIns.length} prior check-in
                        {existingCheckIns.length !== 1 ? "s" : ""})
                        {totalReceivedThisBatch > 0 && (
                          <span className="ml-1 text-gray-500">
                            &middot; +{totalReceivedThisBatch} this batch / {totalRemaining} remaining
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-5">
                  <Table
                    columns={productTableColumns}
                    data={formData.products}
                    rowHighlight={(row: EuRelabelCheckInProductRow): TableRowHighlight | undefined => {
                      const totalReceived = row.prevReceivedQty + (Number(row.receivedQty) || 0);
                      if (totalReceived === 0) return undefined;
                      if (totalReceived > row.totalExpected) return "danger";
                      const gxoEuCheckIn = gxoEuCheckInQtyByProductId[row.id];
                      if (gxoEuCheckIn !== undefined) {
                        return totalReceived >= gxoEuCheckIn ? "success" : "info";
                      }
                      return totalReceived === row.totalExpected ? "success" : "info";
                    }}
                  />

                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <ControlButton onClick={handleReceivedAllRemaining} text="Receive All Remaining" />
                  </div>

                  <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("products")} />
                </div>
              </div>

              {newCheckIn && (newCheckIn.routingInstructions.length > 0 || newCheckIn.shortages.length > 0) && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-black uppercase tracking-wide">New EU Check-In</h3>
                  {newCheckIn.routingInstructions.length > 0 && (
                    <RoutingInstructionCards routingInstructions={newCheckIn.routingInstructions} />
                  )}
                  <LoggedShortagesList shortages={newCheckIn.shortages} />

                  {newCheckIn.routingInstructions.length > 0 && (
                    <div className="flex justify-center pt-2">
                      <Link
                        to={`${ROUTES_CONFIG.relabelCaseBuilder.path}?id=${sourceOrderId}`}
                        className="inline-flex items-center gap-2 rounded-lg bg-black px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2"
                      >
                        Relabel Case Builder
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  )}
                </div>
              )}

              {showOverageSubmitError && (
                <div
                  ref={overageErrorRef}
                  className="max-w-2xl rounded-lg border border-red-200 bg-red-50 p-4 text-left text-red-900 shadow-sm"
                  role="alert"
                >
                  <div className="flex gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                    </div>
                    <div className="min-w-0 flex-1 space-y-2">
                      <h3 className="text-sm font-semibold text-red-950">Quantity exceeds remaining</h3>
                      <p className="text-sm text-red-800">
                        One or more products exceed the remaining quantity for this {isPO ? "PO" : "TO"}:
                      </p>
                      <ul className="list-none space-y-1 text-sm text-red-800">
                        {overageProducts.map((p) => (
                          <li key={p.id}>
                            <span className="font-mono font-semibold">{p.id}</span>
                            <span className="text-red-700">
                              {" "}
                              — receiving {p.receivedQty}, remaining {p.remaining} (+
                              {Number(p.receivedQty) - p.remaining} over)
                            </span>
                          </li>
                        ))}
                      </ul>
                      <p className="text-sm text-red-800">
                        Reduce received quantities to the remaining amount, or raise a Child PO. Contact Yasin{" "}
                        <a
                          href={`mailto:${CHILD_PO_DEFAULT_SENDER_EMAIL}`}
                          className="font-medium text-red-950 underline hover:no-underline"
                        >
                          {CHILD_PO_DEFAULT_SENDER_EMAIL}
                        </a>{" "}
                        for further details.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <FormLvlErrorMessage message={formLvlErrorMessage} centred={false} ref={formLvlErrorMessageRef} />

              <div className="flex items-center gap-4">
                <button
                  className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                  disabled={!isFormUpdated}
                  type="submit"
                >
                  Confirm Receipt & Calculate Routing
                </button>
              </div>

              {previousCheckIns.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide">
                    Previous EU Check-Ins ({previousCheckIns.length})
                  </h3>
                  {previousCheckIns.map((checkIn) => (
                    <CollapsibleSection
                      key={checkIn.id}
                      title={
                        <span className="flex items-center gap-2">
                          {checkIn.createdAt ? new Date(checkIn.createdAt).toLocaleString() : "—"} —{" "}
                          {checkIn.totalReceivedUnits} units
                          {checkIn.shortages.length > 0 && (
                            <span className="text-xs text-amber-700 font-semibold">
                              ({checkIn.shortages.length} shortage{checkIn.shortages.length !== 1 ? "s" : ""} logged)
                            </span>
                          )}
                        </span>
                      }
                      variant="default"
                    >
                      <div className="px-6 pb-5 pt-2 space-y-4">
                        <RoutingInstructionCards routingInstructions={checkIn.routingInstructions} />
                        <LoggedShortagesList shortages={checkIn.shortages} />
                      </div>
                    </CollapsibleSection>
                  ))}
                </div>
              )}
            </div>
          </form>
        </div>
      </div>

      {shortageModalRow && (
        <LogShortageModal
          open
          onClose={() => setShortageModalProductId(null)}
          productSkuName={shortageModalRow.skuName}
          productName={shortageModalRow.name}
          expectedQty={shortageModalRow.remaining}
          receivedQty={Number(shortageModalRow.receivedQty) || 0}
          prevShortageQty={shortageModalRow.prevShortageQty}
          initialShortage={shortageModalRow.shortage}
          onSubmit={(input) => {
            const productIndex = formData.products.findIndex((p) => p.id === shortageModalRow.id);
            handleProductShortageChange(productIndex, input);
            setShortageModalProductId(null);
          }}
          onClear={() => {
            const productIndex = formData.products.findIndex((p) => p.id === shortageModalRow.id);
            handleProductShortageChange(productIndex, undefined);
            setShortageModalProductId(null);
          }}
        />
      )}
    </div>
  );
};

export { EuRelabelCheckInForm };
